import classes from './Details.module.sass'
import { PromoInterface } from '../../../../../common/types/interfaces'
import { useSelector } from 'react-redux'
import { selectClusterImageUrl } from '../../../../../store/slices/currentClusterSlice'
import { format } from 'date-fns'

type Props = {
  el?: PromoInterface
}

export const Details = ({ el }: Props) => {
  const imageUrl = useSelector(selectClusterImageUrl)

  if (!el) return null

  const summary = el.mailingSummary

  const renderStatus = (value: boolean) => {
    return value ? '✅ Да' : '❌ Нет'
  }

  const getMailingStatusText = () => {
    switch (summary?.status) {
      case 'in_progress':
        return '🔄 Идёт рассылка'
      case 'completed':
        return '✅ Рассылка завершена'
      case 'not_started':
        return '⏳ Не запущена'
      default:
        return null
    }
  }

  const renderFile = (path: string) => {
    const fullUrl = `${imageUrl}${path}`

    if (path.includes('video_')) {
      return (
        <video
          className={classes.video}
          src={fullUrl}
          controls
          muted
          playsInline
          poster={
            el.files?.find((p) => p.includes('poster_'))
              ? `${imageUrl}${el.files.find((p) => p.includes('poster_'))}`
              : undefined
          }
        />
      )
    }

    if (path.includes('poster_')) {
      return null
    }

    return <img className={classes.image} src={fullUrl} alt={el.name} />
  }

  return (
    <div className={classes.container}>
      <div className={classes.title}>детали промо акции</div>

      <div className={classes.name}>{el.name}</div>
      <div className={classes.description}>{el.description || 'Нет описания'}</div>

      {/* Блок рассылки */}
      {summary && (
        <div className={classes.mailingBlock}>
          <div className={classes.mailingTitle}>📧 Статус рассылки</div>
          <div className={classes.mailingStatus}>{getMailingStatusText()}</div>
          {summary.status !== 'not_started' && (
            <div className={classes.mailingStats}>
              <div>
                📨 Отправлено: {summary.sentCount} / {summary.totalCount}
              </div>
              <div>👁️ Открытий: {summary.openedCount}</div>
              {summary.completedAt && (
                <div>📅 Завершена: {format(new Date(summary.completedAt), 'dd.MM.yyyy HH:mm')}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Основная информация */}
      <div className={classes.sectionTitle}>Основная информация</div>
      <div className={classes.infoRow}>
        <div className={classes.infoLabel}>ID</div>
        <div className={classes.infoValue}>{el.id}</div>
      </div>
      <div className={classes.infoRow}>
        <div className={classes.infoLabel}>Скидка</div>
        <div className={classes.infoValue}>{el.discount}%</div>
      </div>
      {el.discountCode && (
        <div className={classes.infoRow}>
          <div className={classes.infoLabel}>Промокод</div>
          <div className={classes.infoValue}>{el.discountCode}</div>
        </div>
      )}

      {/* Статусы */}
      <div className={classes.sectionTitle}>Статусы</div>
      <div className={classes.infoRow}>
        <div className={classes.infoLabel}>Активна</div>
        <div className={classes.infoValue}>{renderStatus(el.isActive)}</div>
      </div>
      <div className={classes.infoRow}>
        <div className={classes.infoLabel}>Показывать клиентам</div>
        <div className={classes.infoValue}>{renderStatus(el.isVisible)}</div>
      </div>

      {/* Даты */}
      {el.datesImportant && (
        <>
          <div className={classes.sectionTitle}>Сроки действия</div>
          {el.startDate && (
            <div className={classes.infoRow}>
              <div className={classes.infoLabel}>Дата начала</div>
              <div className={classes.infoValue}>
                {format(new Date(el.startDate), 'dd.MM.yyyy HH:mm')}
              </div>
            </div>
          )}
          {el.endDate && (
            <div className={classes.infoRow}>
              <div className={classes.infoLabel}>Дата завершения</div>
              <div className={classes.infoValue}>
                {format(new Date(el.endDate), 'dd.MM.yyyy HH:mm')}
              </div>
            </div>
          )}
        </>
      )}

      {/* Целевая аудитория */}
      {el.useItems && el.itemsFor && el.itemsFor.length > 0 && (
        <>
          <div className={classes.sectionTitle}>На что</div>
          <div className={classes.infoRow}>
            <div className={classes.infoLabel}>Товары</div>
            <div className={classes.tags}>
              {el.itemsFor.map((item, idx) => (
                <span key={idx} className={classes.tag}>
                  {item}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {el.useCategories && el.categoriesFor && el.categoriesFor.length > 0 && (
        <div className={classes.infoRow}>
          <div className={classes.infoLabel}>Категории</div>
          <div className={classes.tags}>
            {el.categoriesFor.map((cat, idx) => (
              <span key={idx} className={classes.tag}>
                {cat}
              </span>
            ))}
          </div>
        </div>
      )}

      {el.useStores && el.storesFor && el.storesFor.length > 0 && (
        <div className={classes.infoRow}>
          <div className={classes.infoLabel}>Торговые точки</div>
          <div className={classes.tags}>
            {el.storesFor.map((store, idx) => (
              <span key={idx} className={classes.tag}>
                {store}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Изображения */}
      {el.files && el.files.length > 0 && (
        <>
          <div className={classes.sectionTitle}>Медиа</div>
          <div className={classes.images}>
            {el.files.map((path, index) => (
              <div key={index} className={classes.mediaItem}>
                {renderFile(path)}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
